export const COR_COLETA_VERDE = '#d4edda';
export const COR_COLETA_AMARELO = '#fff3cd';
export const COR_COLETA_VERMELHO = '#f8d7da';
export const COR_COLETA_AUSENTE = '#ccc';

const LIMITE_VERDE_PERCENTUAL = 90;
const LIMITE_MINIMO_PADRAO_PERCENTUAL = 80;
const TIPO_COLETA = 7;
const TIPO_INTEGRACAO = 8;

export interface ClassificacaoDiaParams {
  quantidade: number | null;
  esperado: number | null;
  minimoRegistros: number | null;
  percentual: number | null;
  fallback?: string;
}

function numeroFinito(valor: unknown): number | null {
  if (valor == null || valor === '') {
    return null;
  }
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : null;
}

function numeroPositivo(valor: unknown): number | null {
  const numero = numeroFinito(valor);
  return numero != null && numero > 0 ? numero : null;
}

export function parsePercentualColeta(valor: unknown): number | null {
  if (valor == null || valor === '') {
    return null;
  }
  const percentual = parseFloat(String(valor).replace('%', '').replace(',', '.').trim());
  return Number.isFinite(percentual) ? percentual : null;
}

function mesmoDia(dataResumo: unknown, dia: string): boolean {
  if (dataResumo == null || dataResumo === '') {
    return false;
  }
  return String(dataResumo).slice(0, 10) === dia.slice(0, 10);
}

function integracaoDesligada(rowData: any): boolean {
  const valor = rowData?.desativar_integracao;
  return valor === true || valor === 1 || valor === '1' || valor === 't' || valor === 'true';
}

function criterioMinimoRegistros(configuracoes: any[] | null | undefined, usarColeta: boolean): number | null {
  const leitura = (tipo: number): number | null => {
    const cfg = (configuracoes || []).find((item) => Number(item?.configuracao_evento_id) === tipo);
    return numeroPositivo(cfg?.criterio_diario);
  };

  if (usarColeta) {
    return leitura(TIPO_COLETA);
  }

  return leitura(TIPO_INTEGRACAO) ?? leitura(TIPO_COLETA);
}

export function classificarCorColeta(params: ClassificacaoDiaParams): string {
  const fallback = params.fallback ?? COR_COLETA_AUSENTE;
  const esperado = numeroPositivo(params.esperado);
  const quantidade = numeroFinito(params.quantidade);
  const minimo = numeroPositivo(params.minimoRegistros);
  const percentualInformado = numeroFinito(params.percentual);

  const percentual = quantidade != null && esperado != null
    ? (quantidade / esperado) * 100
    : percentualInformado;

  if (percentual == null) {
    return fallback;
  }

  const abaixoDoMinimo = quantidade != null && minimo != null
    ? quantidade < minimo
    : percentual < (minimo != null && esperado != null
      ? (minimo / esperado) * 100
      : LIMITE_MINIMO_PADRAO_PERCENTUAL);

  if (abaixoDoMinimo) {
    return COR_COLETA_VERMELHO;
  }
  if (percentual > LIMITE_VERDE_PERCENTUAL) {
    return COR_COLETA_VERDE;
  }
  return COR_COLETA_AMARELO;
}

export function corFundoDiaMonitoramento(
  rowData: any,
  dataResumo: string,
  opcoes?: { fallback?: string; exigirAtivo?: boolean }
): string {
  const fallback = opcoes?.fallback ?? COR_COLETA_AUSENTE;
  const dia = String(dataResumo ?? '').slice(0, 10);
  const eventos = rowData?.eventos || [];
  const eventoColeta = eventos.find((evento: any) =>
    Number(evento?.tipo_evento_id) === TIPO_COLETA && mesmoDia(evento?.data_resumo, dia)
  );

  if (!eventoColeta || (opcoes?.exigirAtivo && !eventoColeta.ativo)) {
    return fallback;
  }

  const usarColeta = integracaoDesligada(rowData);
  const eventoIntegracao = eventos.find((evento: any) =>
    Number(evento?.tipo_evento_id) === TIPO_INTEGRACAO && mesmoDia(evento?.data_resumo, dia)
  );
  const origem = usarColeta ? eventoColeta : (eventoIntegracao || eventoColeta);
  const percentualBruto = usarColeta
    ? (eventoColeta.percentual_coleta || eventoColeta.percentual_integracao)
    : eventoColeta.percentual_integracao;

  const quantidade = (usarColeta || eventoIntegracao)
    ? numeroFinito(origem?.total_computado_normal)
    : null;

  return classificarCorColeta({
    quantidade,
    esperado: numeroPositivo(origem?.maximo_normalidade_eventos)
      ?? numeroPositivo(eventoColeta?.maximo_normalidade_eventos),
    minimoRegistros: criterioMinimoRegistros(rowData?.configuracoes_evento, usarColeta),
    percentual: parsePercentualColeta(percentualBruto),
    fallback,
  });
}
